import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import api from "../../api";
import { usePurchaseOrdersHeader } from "./Components/PurchaseOrdersHeader/usePurchaseOrdersHeader";
import { useItemsBlock } from "./Components/ItemsBlock/useItemsBlock";
import { capitalizeFirstLetter, getUserId } from "../../helperFunctions";
import { showToast } from "../../Components/Toast";
import { nanoid } from "nanoid";
import {
  validateItemsBlock,
  validateItemsBlock_QtyInput,
  validateItemsBlock_RecivedInput,
} from "../../formsValidation";
import { useApproveVendorModal } from "./Components/VendorsBlock/Components/ApproveVendorModal/useApproveVendorModal";

// A vendor's orderAmount is stored as a combined string like "1234.50 USD".
// Split it into { amount, currency } so the PO-level Order Amount block can be
// populated from the approved vendor. Falls back to USD when no currency suffix.
const KNOWN_CURRENCIES = [
  "USD", "EUR", "GBP", "AED", "DKK", "CNY", "JPY",
  "SGD", "TRY", "AUD", "CAD", "CHF", "INR", "RUB",
];
const splitVendorOrderAmount = (raw) => {
  if (raw === undefined || raw === null || raw === "") {
    return { amount: "", currency: "" };
  }
  const parts = String(raw).trim().split(/\s+/);
  const last = parts[parts.length - 1]?.toUpperCase();
  if (parts.length > 1 && KNOWN_CURRENCIES.includes(last)) {
    return { amount: parts.slice(0, -1).join(" "), currency: last };
  }
  return { amount: String(raw).trim(), currency: "USD" };
};

export let usePurchaseOrders = create(
  immer((set, get) => ({
    section: "all purchaseOrders",

    formMode: undefined,

    //Pagination
    purchaseOrdersArrayLength: 0,
    invoicesData: [],

    form: {
      date: new Date(),
      status: "draft",
      dryDock: false,
      certExpDate: undefined,
      bestETA: undefined,
      attachedFiles: [],
      attachedInvoices: [],
      componentName: "",
      orderAmount: "",
      orderAmountCurrency: "",
      message: undefined,
      items: [
        {
          id: nanoid(),
          partId: undefined,
          otherPart: undefined,
          partName: "",
          partNo: "",
          catalogue: "",
          unit: "",
          qty: "",
          rob: "",
          received: "",
        },
      ],
    },

    itemsPerPage: 10,
    activePage: 1,
    //Sort
    sortColumn: "issued",
    sortSettings: { ["issued"]: "desc" }, //desc

    handleSort: (data, type) => {
      set({ sortSettings: { [data]: type } });
      get().getPurchaseOrders();
      set({ sortColumn: data });
    },

    dateHandler: (date) => {
      set((state) => {
        state.form.date = date;
      });
    },

    certExpDateHandler: (date) => {
      set((state) => {
        state.form.certExpDate = date;
      });
    },

    bestETAHandler: (date) => {
      set((state) => {
        state.form.bestETA = date;
      });
    },

    accountHandler: (value) => {
      set({ form: { ...get().form, accountId: value } });
    },

    locationHandler: (value) => {
      set({ form: { ...get().form, locationId: value } });
    },

    departmentHandler: (department) => {
      set((state) => {
        state.form.department = department;
      });
    },

    priorityHandler: (priority) => {
      set((state) => {
        state.form.priority = priority;
      });
    },

    requisitionTypeHandler: (type) => {
      set((state) => {
        state.form.type = type;
      });
    },
    orderAmountHandler: (orderAmount) => {
      set((state) => {
        state.form.orderAmount = orderAmount;
      });
    },

    orderAmountCurrencyHandler: (orderAmountCurrency) => {
      set((state) => {
        state.form.orderAmountCurrency = orderAmountCurrency;
      });
    },

    showCreatePurchaseOrder: () => {
      get().clear();
      set({ section: "form", formMode: "create" });
    },

    showEditPurchaseOrder: async (purchaseOrderId) => {
      let form = await api.purchaseOrder.getById(purchaseOrderId);
      if (form) set({ form, section: "form", formMode: "edit" });
    },

    cancel: async () => {
      set({ section: "all purchaseOrders" });

      await get().getPurchaseOrders();
      get().clear();
    },

    formHandler: (form) => {
      set({ form });
    },

    countryHandler: (value) => {
      set((state) => {
        state.form.country = value;
      });
    },

    typeHandler: (value) => {
      set((state) => {
        state.form.typeId = value;
      });
    },

    componentHandler: (value) => {
      set((state) => {
        //state.form.componentId = value;
        state.form.componentName = value;
      });
    },

    dryDockHandler: () => {
      set((state) => {
        state.form.dryDock = !state.form.dryDock;
      });
    },

    onManufactureChange: async (manufacturerId) => {
      if (manufacturerId?.length != 24) return;

      set((state) => {
        state.form.manufacturerId = manufacturerId;
        //state.form.otherManufacturer = undefined;
        return state;
      });
    },
    onManufacturerChangeName: (value) => {
      set((state) => {
        state.form.manufacturerName = value;
        state.form.otherManufacturer = undefined;
        return state;
      });
    },
    onManufactureCreate: (value) => {
      set((state) => {
        state.form.otherManufacturer = value;
        state.form.manufacturerId = undefined;
        return state;
      });
    },

    itemsHandler: (v) => {
      set({ form: { ...get().form, items: v } });
    },

    purchaseOrders: [],

    getPurchaseOrders: async () => {
      const { activePage, itemsPerPage, sortSettings } = get();
      const { filter, searchValues } = usePurchaseOrdersHeader.getState();

      const options = {
        activePage,
        itemsPerPage,
        ...filter,
        ...searchValues,
        sortSettings,
      };

      let { data, total } = await api.purchaseOrder.getAll(options);

      if (!data?.length) {
        set({ purchaseOrders: [] });
        set({ purchaseOrdersArrayLength: 0 });

        return;
      }

      const withCapitalizedStatus = data.map((order) => {
        order.status = capitalizeFirstLetter(order.status);
        return order;
      });

      set({
        purchaseOrders: data,
        purchaseOrdersArrayLength: total,
      });
    },

    createPurchaseOrder: async () => {
      let { form, getPurchaseOrders, cancel } = get();

      let error = validateItemsBlock(form.items);
      if (error) return showToast("e", "Select part");

      error = validateItemsBlock_QtyInput(form.items);
      if (error) return showToast("e", "Item QTY is empty");

      // Strip read-only display field (see updatePurchaseOrder).
      const { itemVendorPrices, ...formToSave } = form;

      let response = await api.purchaseOrder.create(formToSave);
      if (response === true) {
        await getPurchaseOrders();
        cancel();
      }
    },

    updatePurchaseOrder: async () => {
      let { form, getPurchaseOrders, cancel, message } = get();

      let error = validateItemsBlock(form.items);
      if (error) return showToast("e", "Select part");

      error = validateItemsBlock_QtyInput(form.items);
      if (error) return showToast("e", "Item QTY is empty");

      let purchaseOrderId = form._id;
      if (message) form.message = message;

      // itemVendorPrices is read-only display data merged in from the separate
      // vendorPrice collection — it is not a PO field, so strip it before save
      // (the backend update schema rejects unknown fields).
      const { itemVendorPrices, ...formToSave } = form;

      let response = await api.purchaseOrder.updateById(
        purchaseOrderId,
        formToSave
      );
      if (response === true) {
        set({ countFormChanges: 0 });
        // await getPurchaseOrders();
        // cancel();
      }
    },

    setPOStatus: async (status) => {
      let { form, getPurchaseOrders, cancel } = get();
      let purchaseOrderId = form._id;

      if (status == "items in transit") {
        const foundApproved = form.vendors.some(
          (vendor) => vendor.status === "approved"
        );
        if (!foundApproved)
          return showToast(
            "e",
            "Please approve a vendor before changing the status to 'Items in transit'"
          );
      }

      let response = await api.purchaseOrder.setPOStatus(
        purchaseOrderId,
        status
      );
      if (response === true) {
        await getPurchaseOrders();
        cancel();
      }
    },
    approveByCrewChief: async () => {
      const { getPurchaseOrders, cancel, message, countFormChanges } = get();
      const { _id: purchaseorderId } = get().form;

      if (!purchaseorderId) return showToast("e", "Save form before approving");
      if (countFormChanges > 1)
        return showToast("e", "Save form before approving");

      const response = await api.purchaseOrder.approveByCrewChief(
        purchaseorderId,
        message
      );
      if (response === true) {
        await getPurchaseOrders();
        cancel();
      }
    },

    deletePurchaseOrder: async () => {
      let { form, getPurchaseOrders, cancel } = get();

      let purchaseOrderId = form._id;
      let response = await api.purchaseOrder.deleteById(purchaseOrderId);
      if (response === true) {
        await getPurchaseOrders();
        cancel();
      }
    },

    message: undefined,

    messageHandler: (v) => {
      set({ message: v });
    },

    notApproveByOffice: async () => {
      const { getPurchaseOrders, cancel, message } = get();
      const { _id: purchaseorderId } = get().form;

      const response = await api.purchaseOrder.notApproveByOffice(
        purchaseorderId,
        message
      );
      if (response === true) {
        await getPurchaseOrders();
        cancel();
      }
    },

    approveByOffice: async () => {
      const { getPurchaseOrders, message, cancel } = get();
      const { _id: purchaseorderId } = get().form;

      const response = await api.purchaseOrder.approveByOffice(
        purchaseorderId,
        message
      );
      if (response === true) {
        await getPurchaseOrders();
        cancel();
      }
    },

    rejectByOffice: async () => {
      const { getPurchaseOrders, cancel, message } = get();
      const { _id: purchaseorderId } = get().form;

      const response = await api.purchaseOrder.rejectByOffice(
        purchaseorderId,
        message
      );

      if (response === true) {
        await getPurchaseOrders();
        cancel();
      }
    },

    setActivePage: (page) => {
      set({ activePage: page });
    },

    addVendor: async (vendorId, vendorName) => {
      const { _id: purchaseOrderId, vendors } = get().form;

      const arr = vendors.filter(
        (vendor) => vendor.id == vendorId && vendor.status != "cancelled"
      );
      if (arr.length) return showToast("e", "Vendor already in the list");

      const vendor = {
        vendorIndex: nanoid(8),
        id: vendorId,
        name: vendorName,
        status: "not inquired",
        inquredDate: undefined,
      };

      const response = await api.purchaseOrder.addVendor(
        purchaseOrderId,
        vendor
      );

      if (response === true) {
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ form, countFormChanges: 0 });
      }
    },

    deleteVendor: async (vendorIndex, vendorId, vendorName) => {
      const { _id: purchaseOrderId } = get().form;

      const vendor = { id: vendorId, name: vendorName, vendorIndex };

      const response = await api.purchaseOrder.deleteVendor(
        purchaseOrderId,
        vendor
      );

      if (response === true) {
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ form, countFormChanges: 0 });
      }
    },

    inquire: async (vendorIndex, vendorId, vendorName, emailBody, emailFields) => {
      const { _id: purchaseOrderId } = get().form;

      // emailFields (optional) carries the edited recipient/subject overrides
      // from the preview modal: { email, cc_email_1, cc_email_2, subject }.
      const vendor = {
        id: vendorId,
        name: vendorName,
        vendorIndex,
        emailBody,
        ...(emailFields || {}),
      };

      const response = await api.purchaseOrder.inquire(purchaseOrderId, vendor);

      if (response === true) {
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ form, countFormChanges: 0 });
        set({ loading: false });
      }
    },
    changeVendorOrderAmount: async (vendorIndex, vendorId, orderAmount) => {
      const { _id: purchaseOrderId } = get().form;

      const vendor = { id: vendorId, orderAmount, vendorIndex };

      const response = await api.purchaseOrder.vendorOrderAmount(
        purchaseOrderId,
        vendor
      );

      if (response === true) {
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ form, countFormChanges: 0 });
        set({ loading: false });
        return true;
      } else return false;
    },
    changeVendorOrderNote: async (vendorIndex, vendorId, orderNote) => {
      const { _id: purchaseOrderId } = get().form;

      const vendor = { id: vendorId, orderNote, vendorIndex };

      const response = await api.purchaseOrder.vendorOrderNote(
        purchaseOrderId,
        vendor
      );

      if (response === true) {
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ form, countFormChanges: 0 });
        set({ loading: false });
        return true;
      } else return false;
    },
    changeVendorDeliveryDate: async (vendorIndex, vendorId, deliveryDate) => {
      const { _id: purchaseOrderId } = get().form;

      const vendor = { id: vendorId, deliveryDate, vendorIndex };

      const response = await api.purchaseOrder.vendorDeliveryDate(
        purchaseOrderId,
        vendor
      );

      if (response === true) {
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ form, countFormChanges: 0 });
        set({ loading: false });
        return true;
      } else return false;
    },
    changeVendorIncludeInComparison: async (
      vendorIndex,
      vendorId,
      includeInComparison
    ) => {
      const { _id: purchaseOrderId } = get().form;

      const vendor = { id: vendorId, includeInComparison, vendorIndex };

      const response = await api.purchaseOrder.vendorIncludeInComparison(
        purchaseOrderId,
        vendor
      );

      if (response === true) {
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ form, countFormChanges: 0 });
        set({ loading: false });
        return true;
      } else return false;
    },
    changeVendorCrossRate: async (vendorIndex, vendorId, crossRate) => {
      const { _id: purchaseOrderId } = get().form;

      const vendor = { id: vendorId, crossRate, vendorIndex };

      const response = await api.purchaseOrder.vendorCrossRate(
        purchaseOrderId,
        vendor
      );

      if (response === true) {
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ form, countFormChanges: 0 });
        set({ loading: false });
        return true;
      } else return false;
    },
    saveVendorItemPrices: async (vendorIndex, vendorId, prices) => {
      const { _id: purchaseOrderId } = get().form;

      const response = await api.purchaseOrder.setVendorItemPrices(
        purchaseOrderId,
        vendorIndex,
        vendorId,
        prices
      );

      if (response === true) {
        // Refetch so itemVendorPrices (merged server-side) is authoritative.
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ form, countFormChanges: 0 });
        return true;
      } else return false;
    },

    approveVendor: async (vendorIndex, vendorId, vendorName) => {
      useApproveVendorModal.getState().setLoading(true);

      const { _id: purchaseOrderId } = get().form;

      const vendor = { id: vendorId, name: vendorName, vendorIndex };

      const response = await api.purchaseOrder.approveVendor(
        purchaseOrderId,
        vendor
      );

      if (response === true) {
        useApproveVendorModal.getState().closeModal();
        let form = await api.purchaseOrder.getById(purchaseOrderId);

        // Populate the PO-level Order Amount block from the just-approved
        // vendor's amount (entered in VendorsBlock as "1234.50 USD").
        const approved =
          form?.vendors?.find((v) => v.id === vendorId) ||
          form?.vendors?.find((v) => v.status === "approved");
        if (approved?.orderAmount) {
          const { amount, currency } = splitVendorOrderAmount(
            approved.orderAmount
          );
          form.orderAmount = amount;
          form.orderAmountCurrency = currency;
        }

        set({ form, countFormChanges: 0 });
      }
    },

    placePO: async (
      vendorIndex,
      vendorId,
      vendorName,
      email,
      subject,
      text,
      a_form,
      action
    ) => {
      useApproveVendorModal.getState().setLoading(true);

      const { _id: purchaseOrderId } = get().form;

      const vendor = { id: vendorId, name: vendorName, vendorIndex };

      const response = await api.purchaseOrder.placePO(
        purchaseOrderId,
        vendor,
        email,
        subject,
        text,
        a_form,
        action
      );

      if (response === true) {
        useApproveVendorModal.getState().closeModal();
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ loading: false });
        set({ form, countFormChanges: 0 });
      }
    },

    withdrawVendorApproval: async (
      vendorIndex,
      vendorId,
      vendorName,
      email,
      subject,
      text,
      a_form,
      action
    ) => {
      const { _id: purchaseOrderId } = get().form;

      const vendor = { id: vendorId, name: vendorName, vendorIndex };

      const response = await api.purchaseOrder.withdrawVendorApproval(
        purchaseOrderId,
        vendor
      );

      if (response === true) {
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ loading: false });
        set({ form, countFormChanges: 0 });
      }
    },
    submitReceived: async () => {
      const { form, cancel, getPurchaseOrders, message } = get();
      const { _id: purchaseOrderId } = get().form;

      const error = validateItemsBlock_RecivedInput(form.items);
      if (error) return showToast("e", "Item Recived is empty");

      form.message = message;

      // Strip read-only display field (see updatePurchaseOrder).
      const { itemVendorPrices, ...formToSave } = form;

      const response = await api.purchaseOrder.submitReceived(
        purchaseOrderId,
        formToSave
      );

      if (response === true) {
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ loading: false });
        set({ form, countFormChanges: 0 });
      }
    },

    invoiceReceive: async () => {
      const { cancel, getPurchaseOrders, message } = get();
      const { _id: purchaseOrderId } = get().form;

      const response = await api.purchaseOrder.invoiceReceived(
        purchaseOrderId,
        message
      );

      if (response === true) {
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ loading: false });
        set({ form, countFormChanges: 0 });
      }
    },

    archivePO: async () => {
      const { _id: purchaseOrderId } = get().form;

      const response = await api.purchaseOrder.archivePO(purchaseOrderId);

      if (response === true) {
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ loading: false });
        set({ form, countFormChanges: 0 });
      }
    },

    cancelOrder: async () => {
      const { cancel, getPurchaseOrders, message } = get();
      const { _id: purchaseOrderId } = get().form;

      const response = await api.purchaseOrder.cancel(purchaseOrderId, message);

      if (response === true) {
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ loading: false });
        set({ form, countFormChanges: 0 });
      }
    },

    attachFiles: async (newFiles) => {
      set({
        form: {
          ...get().form,
          attachedFiles: [...get().form.attachedFiles, ...newFiles],
        },
      });
    },

    attachQuotationFiles: async (vendorId, vendorIndex, vendorName, files) => {
      const { _id: purchaseOrderId } = get().form;

      const vendor = {
        id: vendorId,
        vendorIndex,
        name: vendorName,
        quotationFiles: files,
      };

      const response = await api.purchaseOrder.attachQuotationFiles(
        purchaseOrderId,
        vendor
      );

      if (response === true) {
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ form, countFormChanges: 0 });
        set({ loading: false });
        return true;
      } else return false;
    },

    deleteAttachedFile: async (fileId) => {
      let attachedFiles = get().form.attachedFiles;
      let updated = attachedFiles.filter((file) => fileId != file.id);
      set({ form: { ...get().form, attachedFiles: updated } });
    },

    attachInvoices: async (newInvoices) => {
      const { _id: purchaseOrderId } = get().form;
      const response = await api.purchaseOrder.attachInvoices(purchaseOrderId, {
        invoices: newInvoices,
      });

      if (response === true) {
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ form });
      }
    },

    deleteAttachedInvoice: async (invoiceId, fileName) => {
      const { _id: purchaseOrderId } = get().form;

      const invoice = { id: invoiceId, fileName: fileName };

      const response = await api.purchaseOrder.deleteInvoice(
        purchaseOrderId,
        invoice
      );

      if (response === true) {
        let form = await api.purchaseOrder.getById(purchaseOrderId);
        set({ form });
      }
    },

    getAttachedInvoicesData: async () => {
      let attachedInvoices = get().form.attachedInvoices;
      if (attachedInvoices.length > 0) {
        let attachedInvoicesData = await api.file.getFilesByIds(
          attachedInvoices
        );
        if (attachedInvoicesData.length > 0)
          set({ invoicesData: attachedInvoicesData });
        else set({ invoicesData: attachedInvoices });
      } else {
        set({ invoicesData: [] });
      }
    },

    tableLoading: false,
    setTableLoading: (state) => {
      set({ tableLoading: state });
    },

    loading: false,

    countFormChanges: 0,
    formWasChanged: false,

    resetFormChanges: () => {
      set({ countFormChanges: 0 });
    },

    generating: false,
    generatingRL: false,

    ongenerateRLPreview: async (purchaseOrderId, requisitionNumber) => {
      set({ generatingRL: true });

      const response = await api.purchaseOrder.generateRLPreview(
        purchaseOrderId
      );
      const filename = `RequisitionForm-${requisitionNumber}-preview.xlsx`;
      const fileBlob = response.data;
      if (fileBlob) {
        //const blob = await response.blob();

        const blob = new Blob([fileBlob], { type: fileBlob.type });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        // const blob = new Blob([fileBlob], { type: fileBlob.type });
        // const url = URL.createObjectURL(blob);
        // window.open(url, '_blank');
      } else {
        showToast("error", "Error generating Rl preview");
      }
      set({ generatingRL: false });
    },

    clear: () => {
      set({
        section: "all purchaseOrders",
        formMode: undefined,
        invoicesData: [],
        form: {
          date: new Date(),
          status: "draft",
          dryDock: false,
          certExpDate: undefined,
          bestETA: undefined,
          attachedFiles: [],
          attachedInvoices: [],
          orderAmount: "",
          orderAmountCurrency: "",
          message: undefined,
          items: [
            {
              id: nanoid(),
              partId: undefined,
              otherPart: undefined,
              partName: "",
              partNo: "",
              catalogue: "",
              unit: "",
              qty: "",
              rob: "",
              received: "",
            },
          ],
        },
        message: undefined,
        loading: false,
        formWasChanged: false,
        countFormChanges: 0,
      });
    },
  }))
);
const blobToArrayBuffer = async (blob) => {
  const arrayBuffer = await blob.arrayBuffer();
  return new Uint8Array(arrayBuffer);
};

const unsubscribe = usePurchaseOrders.subscribe(
  (newState, prevState) => {
    if (prevState.form !== newState.form && newState.formMode) {
      usePurchaseOrders.setState((state) => ({
        countFormChanges: state.countFormChanges + 1,
      }));
    }
  },
  (state) => state.form // This specifies the property to watch for changes
);
